// public-reader/components/PublicFooter.tsx — top-referrer leaderboard.
//
// Renders at the bottom of every public-reader route. Fetches
// GET /api/public/leaderboard (no auth, server-cached for 5min) and shows
// up to 10 referrers as first-name + last-initial only.
//
// The styles are bag-of-tokens since the public reader theme is dynamic
// per scenario — the caller passes accent/ink/etc. as props for parity
// with PublicHeader.

import { useEffect, useState, type CSSProperties } from "react";
import {
  getPublicLeaderboard,
  type LeaderboardEntry,
} from "../../services/referralsApi";

export interface PublicFooterProps {
  accent: string;
  inkMuted: string;
  paperEdge: string;
  paper: string;
}

export default function PublicFooter(props: PublicFooterProps): JSX.Element | null {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicLeaderboard();
        if (cancelled) return;
        setEntries(res.topReferrers);
      } catch {
        if (cancelled) return;
        setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // No data and an error → render nothing. Public reader stays usable.
  if (error || !entries) return null;
  if (entries.length === 0) return null;

  const footer: CSSProperties = {
    padding: "32px 24px 40px",
    borderTop: `1px solid ${props.paperEdge}`,
    background: props.paper,
    color: props.inkMuted,
    fontFamily: "inherit",
  };
  const inner: CSSProperties = {
    maxWidth: 760,
    margin: "0 auto",
  };
  const label: CSSProperties = {
    fontSize: "0.68rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: props.inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 14,
  };
  const heading: CSSProperties = {
    margin: "0 0 14px",
    fontSize: "0.95rem",
    fontStyle: "italic",
    color: props.accent,
  };
  const row: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: "6px 0",
    borderBottom: `1px dashed ${props.paperEdge}`,
    fontSize: "0.88rem",
  };
  const nameStyle: CSSProperties = {
    color: props.accent,
  };
  const statStyle: CSSProperties = {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    color: props.inkMuted,
  };

  return (
    <footer style={footer}>
      <div style={inner}>
        <div style={label}>Top sharers</div>
        <h3 style={heading}>Brought here by readers who shared.</h3>
        <div role="list">
          {entries.map((e, i) => (
            <div key={`${e.firstName}-${i}`} style={row} role="listitem">
              <span style={nameStyle}>
                {e.firstName}
                {e.lastInitial ? ` ${e.lastInitial}` : ""}
              </span>
              <span style={statStyle}>
                {new Intl.NumberFormat("en-US").format(e.lifetimeBonusTurns)}{" "}
                turns earned · {e.readersBrought} reader
                {e.readersBrought === 1 ? "" : "s"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
}
